import { SensorAssignment } from "src/app/models/sensorAssignment.model";
import { FormComponent, IFormDialogData, IOrganizationLevelEntity } from "src/app/shared/form/form.component";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SensorAssignmentService } from "src/app/services/sensorAssignment.service";
import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { forkJoin } from "rxjs";
import { ILookup } from "src/app/shared/form/lookup.model";
import { DateFormField, TimeFormField, NumberFormField, SingleSelectionFormField, FormField, ArrayFormField, ButtonFormField, ToggleFormField } from "src/app/shared/form/formField.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import { ParameterService } from "src/app/services/parameter.service";
import { ParameterLookup, ParameterPercentage } from "src/app/models/parameter.model";
import { SensorService } from "src/app/services/sensor.service";
import { CustomEntityService } from "src/app/services/customEntity.service";
import { AuthService } from "src/app/services";
import { format } from 'date-fns';
import { MeasurementModeEnum } from "src/app/enums/measurementMode.enum";

export interface INodeAssignmentFormDialogData extends IFormDialogData, IOrganizationLevelEntity {
    sensorId?: string;
    sensorName?: string;
    nodeId?: string;
    nodeName?: string;
    isNodeAssignment: boolean;
}

@Component({
  standalone: false,
    selector: 'app-node-assignment-dialog-component',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class NodeAssignmentDialogComponent extends FormComponent<SensorAssignment> implements OnInit {

    nodes: ILookup[];
    sensors: ILookup[];
    parameterList: ParameterLookup[] = [];
    parameters: ParameterPercentage[] = [];
    assignName: string = "";
    userTimeZone: string;

    constructor(dialogRef: MatDialogRef<NodeAssignmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: INodeAssignmentFormDialogData,
        service: SensorAssignmentService,
        protected injector: Injector,
        private parameterService: ParameterService,
        private sensorService: SensorService,
        private authService: AuthService,
        private customEntityService: CustomEntityService,
    ) {
        super(dialogRef, service, data, "Node Assignment" + (data.isNodeAssignment ? (data.nodeName != null ? ` - ${data.nodeName}` : "") : ` - ${data.sensorName ?? data.sensorId}`), injector);
        this.userTimeZone = this.authService.getCurrentUser().userTimeZone;
    }

    ngOnInit() {
        this.isFormLoading = true;
        forkJoin({
            entity: this.getEntity(),
            nodes: this.customEntityService.getEndPointLookupList(),
            sensors: this.sensorService.getAll(),
            parameters: this.parameterService.getAllExcludingNotificationOnly()
        }).subscribe(
            result => {
                this.entity = result.entity;
                this.nodes = result.nodes;
                this.sensors = result.sensors;
                this.parameterList = result.parameters;
                this.mapParameters();
                this.initModel();
                this.initFields();
                this.isFormLoading = false;
            },
            error => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            }
        );
    }

    getNewObject(): SensorAssignment {
        return new SensorAssignment();
    }

    initModel(): void {
        this.entity.endTime = format(this.dateFormatService.getEndOfDay(new Date()), 'HH:mm');

        if (this.isAdd) {
            this.entity.startDate = new Date();
            this.entity.startTime = format(this.dateFormatService.getStartOfDay(new Date()), 'HH:mm');
        }
        else {
            this.parameters.forEach(parameterPercentage => {
                const parameter = this.entity.parameters.find(x => x.parameterId === parameterPercentage.parameterId);
                if (parameter) {
                    parameterPercentage.isSelected = true;
                    parameterPercentage.parameterPercentage = this.isDirectMeasurementParameter(parameterPercentage)
                        ? 100
                        : parameter.parameterPercentage * 100;
                }
            });

            if (this.entity.startDate) {
                const startParsed = this.dateFormatService.userTimezoneToLocalDateAndTime(this.entity.startDate as number);
                this.entity.startDate = this.dateFormatService.parseDateComponents(startParsed.date);
                this.entity.startTime = startParsed.time;
            } else {
                this.entity.startTime = format(this.dateFormatService.getStartOfDay(new Date()), 'HH:mm');
            }
            if (this.entity.endDate) {
                this.entity.endDateToggled = true;
                const endParsed = this.dateFormatService.userTimezoneToLocalDateAndTime(this.entity.endDate as number);
                this.entity.endDate = this.dateFormatService.parseDateComponents(endParsed.date);
                this.entity.endTime = endParsed.time;
            }
        }

        this.entity.parameters = this.parameters;
    }

    validate(): boolean {
        if (this.entity.endDateToggled) {
            const startDate = this.entity.startDate;
            const endDate = this.entity.endDate;
            if (!startDate || !endDate) {
                this.toastService.showError(new Error("Validation Error"), "Start and End dates are required");
                return false;
            }
            const startDateStr = this.dateFormatService.parseUserDateToYMD(
                format(startDate instanceof Date ? startDate : new Date(startDate), this.dateFormatService.canonicalDateFormat)
            );
            const endDateStr = this.dateFormatService.parseUserDateToYMD(
                format(endDate instanceof Date ? endDate : new Date(endDate), this.dateFormatService.canonicalDateFormat)
            );
            const startTime = this.dateFormatService.parseUserTimeToHM(this.entity.startTime as string);
            const endTime = this.dateFormatService.parseUserTimeToHM(this.entity.endTime as string);
            const startDateTime = this.dateFormatService.localDateTimeString(startDateStr, startTime);
            const endDateTime = this.dateFormatService.localDateTimeString(endDateStr, endTime);
            if (new Date(endDateTime) < new Date(startDateTime)) {
                this.toastService.showError(new Error("Validation Error"), "End Date cannot be before Start Date");
                return false;
            }
        }
        return true;
    }

    onParameterInputChange(event: any, field: FormField, index?: number): void {
        if (index != null && this.isDirectMeasurementParameter(this.parameters[index])) {
            this.parameters[index].parameterPercentage = 100;
            return;
        }

        if (event.target.value != null) {
            if (event.target.value > 100) {
                this.parameters[index].parameterPercentage = 100;
            }
            else if (event.target.value < 0) {
                this.parameters[index].parameterPercentage = 0;
            }
        }
    }

    onBeforeSave(model: SensorAssignment): void {
        if (model.parameters) {
            model.parameters = model.parameters.filter(x => x.isSelected);
            model.parameters.forEach(x => {
                if (this.isDirectMeasurementParameter(x)) {
                    x.parameterPercentage = 100;
                }
                x.parameterPercentage /= 100;
            });
        }
        if (this.data.isNodeAssignment) {
            model.endPointId = this.data.nodeId;
        }
        else {
            model.sensorId = this.data.sensorId;
        }
        model.organizationLevelId = this.data.organizationLevelId;

        if (!this.isAdd && !model.startDate) {
            this.toastService.showError(new Error("Validation Error"), "Start date is required");
            return;
        }
        const startDateStr = this.dateFormatService.parseUserDateToYMD(
            format(model.startDate instanceof Date ? model.startDate : new Date(model.startDate as string | number), this.dateFormatService.canonicalDateFormat)
        );
        const startTime = this.dateFormatService.parseUserTimeToHM(model.startTime as string);
        const startDateTime = this.dateFormatService.localDateTimeString(startDateStr, startTime);
        model.startDate = this.dateFormatService.convertStringToTimestamp(startDateTime);

        if (model.endDateToggled) {
            const endDateStr = this.dateFormatService.parseUserDateToYMD(
                format(model.endDate instanceof Date ? model.endDate : new Date(model.endDate as string | number), this.dateFormatService.canonicalDateFormat)
            );
            const endTime = this.dateFormatService.parseUserTimeToHM(model.endTime as string);
            const endDateTime = this.dateFormatService.localDateTimeString(endDateStr, endTime);
            model.endDate = this.dateFormatService.convertStringToTimestamp(endDateTime);
        } else {
            model.endDate = null;
        }
    }

    initFields() {
        const parameterFields: FormField[][] = this.parameters.map(parameterPercentage => [
            new NumberFormField({
                name: 'Percentage [%]',
                property: nameOf<ParameterPercentage>(x => x.parameterPercentage),
                min: 0,
                max: 100,
                readonly: this.isDirectMeasurementParameter(parameterPercentage),
                isVisible: (entity: ParameterPercentage) => {
                    return entity.isSelected;
                },
                onChange: (event: any, field: FormField, index: number) => this.onParameterInputChange(event, field, index)
            }),
            new ToggleFormField({
                name: parameterPercentage.parameterName,
                property: nameOf<ParameterPercentage>(x => x.isSelected),
            }),
        ]);

        this.fields = [
            new SingleSelectionFormField({
                property: this.data.isNodeAssignment ? nameOf<SensorAssignment>(x => x.sensorId) : nameOf<SensorAssignment>(x => x.endPointId),
                name: this.data.isNodeAssignment ? "Device" : "Node",
                options: this.data.isNodeAssignment ? this.sensors : this.nodes,
                columnSpan: new ColumnSpan(1, 1)
            }),

            new DateFormField({
                property: nameOf<SensorAssignment>(x => x.startDate),
                name: "Start Date",
                cssClass: "date-assign",
                columnSpan: new ColumnSpan(1, 2)
            }),
            new TimeFormField({
                property: nameOf<SensorAssignment>(x => x.startTime),
                name: "Start Time", columnSpan: new ColumnSpan(1, 2)
            }),
            new ButtonFormField({
                property: nameOf<SensorAssignment>(x => x.endDateToggled),
                name: this.entity.endDateToggled ? "- End Date" : "+ End Date",
                columnSpan: new ColumnSpan(1, 1),
                cssClass: "date-name-assign",
                required: false,
                onClick: () => {
                    this.entity.endDateToggled = !this.entity.endDateToggled;
                    this.fields.find(field => field.property === nameOf<SensorAssignment>(x => x.endDateToggled)).name = this.entity.endDateToggled ? "- End Date" : "+ End Date";
                }
            }),
            new DateFormField({
                property: nameOf<SensorAssignment>(x => x.endDate),
                cssClass: "date-assign",
                name: "End Date",
                required: false,
                isVisible: () => this.entity.endDateToggled,
                columnSpan: new ColumnSpan(1, 2)
            }),
            new TimeFormField({
                property: nameOf<SensorAssignment>(x => x.endTime),
                name: "End Time",
                required: false,
                isVisible: () => this.entity.endDateToggled,
                columnSpan: new ColumnSpan(1, 2)
            }),

            new ArrayFormField({ property: nameOf<SensorAssignment>(x => x.parameters), name: 'Parameters', items: parameterFields, required: false, columnSpan: new ColumnSpan(1, 1) }),
        ];
    }

    mapParameters(): void {
        this.parameters = this.parameterList.map(param => ({
            isSelected: false,
            parameterId: param.id,
            parameterName: param.name,
            measurementMode: param.measurementMode,
            parameterPercentage: 100
        }));
    }

    private isDirectMeasurementParameter(parameter: ParameterPercentage | undefined): boolean {
        return parameter?.measurementMode === MeasurementModeEnum.Direct;
    }

}
